/*
PURPOSE: Editorial call-to-action pill — warm coral-orange surface, rounded,
optional trailing arrow. The base1 CTA treatment.
USE CASES:
- Home2 section CTAs (scroll-to-creator onClick, or link href).
DOMAIN BOUNDARIES:
- Presentation only. Label passed as children (no copy here).
- Thin styled button/anchor. Deliberately NOT a re-implementation of
  CoralButton's async/loading state machine — the homepage CTAs are
  synchronous scroll/navigation. If an async-loading CTA is ever needed,
  use CoralButton directly rather than growing this primitive.
- No imports from design-sandbox/.
*/

import React from 'react';
import { ArrowRight } from 'lucide-react';

interface CtaPillProps {
  children: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  href?: string;
  withArrow?: boolean;
  className?: string;
}

const PILL_CLASS =
  'inline-flex items-center gap-2 bg-coral-orange hover:bg-coral-orange/90 text-editorial-muted rounded-full px-6 py-3 text-sm font-medium transition-colors';

const CtaPill: React.FC<CtaPillProps> = ({
  children,
  onClick,
  href,
  withArrow = true,
  className,
}) => {
  const content = (
    <>
      <span>{children}</span>
      {withArrow && <ArrowRight size={16} strokeWidth={2} aria-hidden="true" />}
    </>
  );

  if (href) {
    return (
      <a href={href} className={`${PILL_CLASS} ${className ?? ''}`}>
        {content}
      </a>
    );
  }

  return (
    <button type="button" onClick={onClick} className={`${PILL_CLASS} ${className ?? ''}`}>
      {content}
    </button>
  );
};

export default CtaPill;
